import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { Observable, of, tap, catchError, throwError, map } from 'rxjs';
import { ApiService } from './api.service';

export interface CustomerData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  licensePlate: string;
  customerType: string;
  locationId?: string;
  make?: string;
  model?: string;
  year?: string;
}

export interface CustomerApiPayload {
  first_name: string;
  last_name: string;
  email: string;
  contact: string;
  type: string;
  license_plate: string;
  customer_status: string;
  force_create: boolean;
  location_id?: string;
  make?: string;
  model?: string;
  year?: string;
}

export interface CustomerListParams {
  page?: number;
  limit?: number;
  type?: string;
  customer_status?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  name?: string;
  contact?: string;
  sort_by?: string;
  sort_order?: string;
  /** Orders-compatible token: `any` | `none` | discount UUID (logical or version id). */
  discount_applied?: string;
  /** Exact customer UUID(s) for Customers with Discounts filter (comma-separated). */
  id?: string;
}

/**
 * Centralized CustomerService (Injectable) that manages customer-related API calls and utilities:
 * provides creation, retrieval, update, deletion, status changes, profile & statistics fetching,
 * and search helpers consumed by customer-related components (e.g., customer-info, add-customer-modal).
 * @component
 * @selector app-customer-service
 * @example
 *   // Inject into a component or another service
 *   constructor(private customerService: CustomerService) {}
 *   this.customerService.getCustomers({ page: 1 }).subscribe(...);
 * @input {none} none - This service has no Angular @Input() properties; it is injected via Angular DI.
 * @output {Observable<any>} apiResponse - Methods return Observables that emit API responses, arrays, or error notifications which callers subscribe to.
 *
 * Key methods:
 * - createCustomer(customerData: CustomerData, forceCreate?: boolean): Observable<any>
 *   Transforms form data into the API payload, supplies defaults for required fields, optionally forces creation,
 *   and posts to the 'customer' endpoint.
 * - getCustomers(params?: CustomerListParams): Observable<any>
 *   Builds a query string from optional params and retrieves a paginated/filtered customer list.
 * - getCustomer(customerId: string): Observable<any>
 *   Retrieves a single customer's details by ID.
 * - updateCustomer(customerId: string, customerData: any): Observable<any>
 *   Sends updated customer fields to the API for an existing customer.
 * - updateCustomerStatus(customerId: string, status: string): Observable<any>
 *   Updates only the customer_status field for a given customer.
 * - deleteCustomer(customerId: string): Observable<any>
 *   Deletes a customer record via the API.
 * - searchByName(name: string): Observable<any[]>
 *   Validates input length and returns an array of matching customers (shared by multiple components).
 * - searchByPhone(phone: string): Observable<any[]>
 *   Validates input length and returns an array of matching customers by phone.
 * - getCustomerProfile(customerId: string): Observable<any>
 *   Retrieves detailed profile information for a customer.
 * - getCustomerStatistics(): Observable<any>
 *   Fetches aggregated customer statistics.
 *
 * Error handling:
 * - Mutation and fetch methods include catchError that logs errors to the console and rethrows them as Observables
 *   so callers can handle failures appropriately.
 *
 * Lifecycle:
 * - Provided in root (providedIn: 'root') — service is a singleton with no Angular component lifecycle hooks.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  constructor(
    private logger: LoggerService,
    private apiService: ApiService
  ) {}

  /** Normalizes customer rows from list/search API `data` payloads across common key shapes. */
  private customerListRowsFromData(data: any): any[] {
    if (!data || typeof data !== 'object') {
      return [];
    }
    const candidates = [
      data.customer,
      data.customers,
      data.items,
      data.results,
      data.list,
      Array.isArray(data.data) ? data.data : null,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) {
        return c;
      }
      if (c && typeof c === 'object') {
        return [c];
      }
    }
    return [];
  }

  /**
   * Create a new customer by transforming form data into the API payload and posting it to the backend.
   * @example
   * reateCustomer({ firstName: 'Jane', lastName: 'Doe', email: 'jane@example.com', phone: '1234567890', customerType: 'Individual' }, true)
   * Observable<any>
   * @param {CustomerData} customerData - Customer form data to be converted into the API payload.
   * @param {boolean} forceCreate - Whether to force creation when duplicates are detected (default: false).
   * @returns {Observable<any>} Observable that emits the API response for the created customer.
   **/
  createCustomer(customerData: CustomerData, forceCreate = false): Observable<any> {
    // Transform the form data to match the API payload format
    // Provide defaults for required fields if empty (API requires non-empty values)
    const apiPayload: CustomerApiPayload = {
      first_name: customerData.firstName || '',
      last_name: customerData.lastName || '', // API requires non-empty, use default if empty
      email: customerData.email || '',
      contact: customerData.phone || '', // API requires non-empty, use default if empty
      type: customerData.customerType.toLowerCase(),
      license_plate: customerData.licensePlate || '',
      customer_status: 'active',
      force_create: forceCreate,
    };

    // Add location_id if provided
    if (customerData.locationId) {
      apiPayload.location_id = customerData.locationId;
    }

    // Add vehicle make, model, and year if provided
    if (customerData.make) {
      apiPayload.make = customerData.make;
    }
    if (customerData.model) {
      apiPayload.model = customerData.model;
    }
    if (customerData.year) {
      apiPayload.year = customerData.year;
    }

    return this.apiService.post('customer', apiPayload).pipe(
      tap((response: any) => {}),
      catchError(error => {
        this.logger.error('Error creating customer:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Retrieve a list of customers, optionally filtered by query parameters.
   * @example
   * etCustomers({ search: 'john', page: 2 })
   * Observable<any>
   * @param {{CustomerListParams}} params - Optional parameters to filter and paginate the customer list.
   * @returns {{Observable<any>}} Observable that emits the API response containing the customer list.
   **/
  getCustomers(params?: CustomerListParams): Observable<any> {
    const searchParams: Record<string, string | number | boolean> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams[key] = typeof value === 'boolean' ? value : value.toString();
        }
      });
    }
    if (params?.name) {
      return this.apiService.get('customer/search', {
        name: params.name,
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        ...(params.sort_by ? { sort_by: params.sort_by } : {}),
        ...(params.sort_order ? { sort_order: params.sort_order } : {}),
      });
    }
    return this.apiService.get('customer', searchParams);
  }

  getCustomer(customerId: string): Observable<any> {
    return this.apiService.get(`customer/${customerId}`);
  }

  updateCustomer(customerId: string, customerData: any): Observable<any> {
    return this.apiService.put(`customer/${customerId}`, customerData).pipe(
      tap((response: any) => {}),
      catchError(error => {
        this.logger.error('Error updating customer:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates the status of a customer by calling the API and returns an observable of the response.
   * @example
   * pdateCustomerStatus('cust123', 'active')
   * Observable<any>
   * @param {{string}} {{customerId}} - The unique identifier of the customer.
   * @param {{string}} {{status}} - The new status to set for the customer.
   * @returns {{Observable<any>}} Observable that emits the API response for the update operation.
   **/
  updateCustomerStatus(customerId: string, status: string): Observable<any> {
    // Create the payload according to the API specification
    const payload = {
      customer_status: status,
    };

    return this.apiService.put(`customer/${customerId}`, payload).pipe(
      tap((response: any) => {}),
      catchError(error => {
        this.logger.error('Error updating customer status:', error);
        return throwError(() => error);
      })
    );
  }

  deleteCustomer(customerId: string): Observable<any> {
    return this.apiService.delete(`customer/delete/${customerId}`);
  }

  /**
   * Search customers by name — returns the customer array from the API response.
   * Shared by customer-info and add-customer-modal components.
   */
  searchByName(name: string): Observable<any[]> {
    if (!name || name.trim().length < 2) {
      return of([]);
    }
    return this.getCustomers({ name }).pipe(
      map((response: any) =>
        response.status && response.data ? this.customerListRowsFromData(response.data) : []
      ),
      catchError(() => of([]))
    );
  }

  /**
   * Search customers by phone — returns the customer array from the API response.
   * Shared by customer-info and add-customer-modal components.
   */
  searchByPhone(phone: string): Observable<any[]> {
    if (!phone || phone.trim().length < 3) {
      return of([]);
    }
    return this.getCustomers({ contact: phone }).pipe(
      map((response: any) =>
        response.status && response.data ? this.customerListRowsFromData(response.data) : []
      ),
      catchError(() => of([]))
    );
  }

  getCustomerByLicensePlate(licensePlate: string): Observable<any> {
    return this.apiService.get('customer/by-license-plate', { license_plate: licensePlate });
  }

  getCustomerProfile(customerId: string): Observable<any> {
    return this.apiService.get(`customer/${customerId}`);
  }

  /**
   * Fetches customer statistics.
   * @example
   * etCustomerStatistics()
   * Observable<any>
   * @returns {Observable<any>} Observable that emits the customer statistics response.
   **/
  getCustomerStatistics(): Observable<any> {
    return this.apiService.get('customer/statistics').pipe(
      tap((response: any) => {}),
      catchError(error => {
        this.logger.error('Error fetching customer statistics:', error);
        return throwError(() => error);
      })
    );
  }
}
